import { defineStore } from 'pinia'
import { nextTick, ref } from 'vue'
import { UseKftContext } from '~/composables/useKftContext'

export type KftNotificationType = 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR'

export type KftNotification =
  | {
      id?: string
      title?: string
      message: string
      type: KftNotificationType
      showIcon?: boolean
      htmlContent?: string
      persist?: boolean
    }
  | {
      id?: string
      title?: string
      message?: string
      type: KftNotificationType
      showIcon?: boolean
      htmlContent: string
      persist?: boolean
    }

export const useNotificationsStore = defineStore('notifications', () => {
  const { $scrollToTop } = UseKftContext()

  const notifications = ref<KftNotification[]>([])

  const addNotification = (
    notification: KftNotification,
    { shouldScrollToTop }: { shouldScrollToTop?: boolean } = {
      shouldScrollToTop: true,
    }
  ) => {
    notifications.value.push(notification)

    if (shouldScrollToTop && $scrollToTop) {
      nextTick(() => $scrollToTop())
    }
  }

  const handleCloseNotification = () => {
    if (notifications.value.length > 0) {
      const lastNotification = notifications.value.pop()

      if (lastNotification?.id) {
        const closedNotifications = JSON.parse(
          localStorage.getItem('closedNotifications') || '{}'
        ) as Record<string, number>

        const DAYS = 2
        const canBeVisibleAgain =
          new Date().getTime() + 1000 * 60 * 60 * 24 * DAYS

        closedNotifications[lastNotification.id] = canBeVisibleAgain

        localStorage.setItem(
          'closedNotifications',
          JSON.stringify(closedNotifications)
        )
      }
    }
  }

  const deleteNotification = (id: string) => {
    notifications.value = notifications.value.filter((n) => n.id !== id)
  }

  const clearNonPersistentNotifications = () => {
    notifications.value = notifications.value.filter((n) => n.persist)
  }

  return {
    notifications,
    addNotification,
    handleCloseNotification,
    deleteNotification,
    clearNonPersistentNotifications,
  }
})
